import type { RunSkill } from "../api/getRunSkills";

/**
 * Slash-command resolution for the composer. A draft may open with a
 * `/<token>` referencing one of the user's skills — selecting it from the
 * typeahead or typing it by hand scopes the turn to that skill. Skills have no
 * stored slug, so we derive one from the name (lowercased, non-alphanumerics
 * collapsed to single hyphens) and also accept the raw name.
 */

/** Derive a URL-ish, hyphenated token from a skill name (no leading slash). */
export function skillSlug(name: string): string {
  return name
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/**
 * The leading slash token of a draft (without the `/`), or null when the draft
 * doesn't open a slash command. Only the first whitespace-delimited token is
 * considered — `/foo bar` yields `foo`.
 */
export function leadingSlashToken(draft: string): string | null {
  if (!draft.startsWith("/")) return null;
  const token = draft.slice(1).split(/\s/, 1)[0] ?? "";
  return token;
}

/**
 * The token the user is still actively typing — the leading slash token ONLY
 * while no whitespace has been added after it. Once the user types a space the
 * command is "committed" and the typeahead must close (and stop swallowing
 * Enter). `/foo` -> "foo"; `/` -> ""; `/foo ` or `/foo bar` -> null.
 */
export function activeSlashToken(draft: string): string | null {
  const match = /^\/(\S*)$/.exec(draft);
  return match ? match[1]! : null;
}

/** Normalize a token for matching: trim, lowercase, slugify. */
function normalize(token: string): string {
  return skillSlug(token);
}

/**
 * Skills whose name or slug starts with the typed token — the typeahead list.
 * An empty token (bare `/`) matches everything so the menu shows the full set.
 */
export function filterSkillsByToken<T extends RunSkill>(skills: T[], token: string): T[] {
  const q = normalize(token);
  if (!q) return skills;
  return skills.filter((s) => {
    const slug = skillSlug(s.name);
    return slug.startsWith(q) || s.name.toLowerCase().startsWith(token.trim().toLowerCase());
  });
}

/**
 * Resolve a draft's leading slash token to exactly one skill (case-insensitive,
 * by slug or name), or null when there's no leading token or no unique match.
 */
export function resolveSlashSkill<T extends RunSkill>(draft: string, skills: T[]): T | null {
  const token = leadingSlashToken(draft);
  if (token === null || token === "") return null;
  const q = normalize(token);
  const lower = token.trim().toLowerCase();
  const matches = skills.filter(
    (s) => skillSlug(s.name) === q || s.name.toLowerCase() === lower,
  );
  return matches.length === 1 ? matches[0]! : null;
}

/**
 * Strip a resolved leading `/<token>` (and the single following space) from the
 * outgoing text. Leaves the draft untouched when it doesn't open with a token.
 */
export function stripLeadingSlashToken(draft: string): string {
  if (!draft.startsWith("/")) return draft;
  return draft.replace(/^\/\S+\s?/, "");
}

/**
 * Replace the leading slash token with `/<slug> ` (typeahead selection),
 * preserving any text already typed after it.
 */
export function applySlashSelection(draft: string, name: string): string {
  const rest = (draft.startsWith("/") ? draft.replace(/^\/\S*/, "") : draft).replace(/^\s+/, "");
  return rest ? `/${skillSlug(name)} ${rest}` : `/${skillSlug(name)} `;
}
